const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

export default function PersonalisationModal({
  show,
  product,
  getPrimaryImage,
  requiresPhotoUpload,
  allowsCustomText,
  customTextLabel,
  customText,
  customTextMaxLength,
  setCustomText,
  uploadedFiles,
  uploadingFiles,
  handleUploadClick,
  removeUploadedFile,
  isPhotoPrintProduct,
  displayTotal,
  unitPrice,
  addPersonalisedToCartHandler,
  setShowPersonaliseModal,
}) {
  if (!show || !product) return null;

  const isPersonalisationIncomplete =
    (requiresPhotoUpload && uploadedFiles.length === 0) ||
    (allowsCustomText && customText.trim().length === 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-black/10 bg-white px-5 py-4">
          <div>
            <h2 className="text-xl font-semibold text-[#2d2d2d]">
              Personalise {product.name}
            </h2>
            <p className="mt-1 text-sm text-black/55">
              Complete your personalization before adding to cart
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowPersonaliseModal(false)}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-black/5 text-2xl font-semibold text-black transition hover:bg-black/10 active:scale-95"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="space-y-6 px-5 py-5 md:px-6 md:py-6">
          <div className="overflow-hidden">
            <div className="flex w-full items-center justify-center">
              <img
                src={getPrimaryImage(product)}
                alt={product.name}
                className="max-h-[320px] w-auto max-w-full object-contain md:max-h-[420px]"
              />
            </div>
          </div>

          {requiresPhotoUpload && (
            <div>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-base font-semibold text-[#2d2d2d]">
                    Upload photos <span className="text-red-500">*</span>
                  </p>
                  <p className="mt-1 text-sm text-black/55">
                    You can upload up to {Number(product.max_uploads || 1)}{" "}
                    image{Number(product.max_uploads || 1) > 1 ? "s" : ""}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleUploadClick}
                  disabled={uploadingFiles}
                  className={[
                    "px-5 py-2.5 text-sm font-semibold text-white transition",
                    uploadingFiles
                      ? "cursor-not-allowed bg-pink-300"
                      : "bg-pink-500 hover:bg-pink-600",
                  ].join(" ")}
                >
                  {uploadingFiles ? "Uploading..." : "Upload photos"}
                </button>
              </div>

              {uploadingFiles && (
                <p className="mt-3 text-sm text-black/60">Uploading image...</p>
              )}

              {uploadedFiles.length > 0 && (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-3">
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={`${file.name}_${index}`}
                        className="relative h-24 w-24 overflow-hidden rounded border border-gray-200 bg-black/5"
                      >
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeUploadedFile(index)}
                          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-xs text-white"
                        >
                          ×
                        </button>
                        {(file.uploaded || file.file_path) && (
                          <span className="absolute bottom-1 right-1 h-2 w-2 rounded-full bg-green-500 ring-1 ring-white" />
                        )}
                      </div>
                    ))}
                  </div>

                  <p className="mt-2 text-xs text-black/55">
                    {uploadedFiles.length} of {Number(product.max_uploads || 1)}{" "}
                    uploaded
                  </p>
                </div>
              )}
            </div>
          )}

          {allowsCustomText && (
            <div>
              <label className="block text-base font-semibold text-[#2d2d2d]">
                {customTextLabel} <span className="text-red-500">*</span>
              </label>

              <textarea
                value={customText}
                onChange={(e) => {
                  const value = e.target.value;

                  if (
                    customTextMaxLength > 0 &&
                    value.length > customTextMaxLength
                  ) {
                    return;
                  }

                  setCustomText(value);
                }}
                rows={4}
                placeholder={customTextLabel}
                className="mt-3 w-full resize-none border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-pink-500"
              />

              {customTextMaxLength > 0 && (
                <p className="mt-2 text-xs text-black/55">
                  {customText.trim().length} / {customTextMaxLength} characters
                </p>
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-black/10 bg-white px-5 py-4 md:px-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-pink-500">
              {isPhotoPrintProduct ? "Total" : "Price"}
            </p>
            <p className="text-2xl font-bold text-black">
              {money(isPhotoPrintProduct ? displayTotal : unitPrice)}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowPersonaliseModal(false)}
              className="border border-black/15 px-5 py-3 text-sm font-semibold hover:bg-black/5"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={addPersonalisedToCartHandler}
              disabled={uploadingFiles || isPersonalisationIncomplete}
              className={[
                "px-5 py-3 text-sm font-semibold text-white transition",
                uploadingFiles || isPersonalisationIncomplete
                  ? "cursor-not-allowed bg-black/40"
                  : "bg-black hover:opacity-90",
              ].join(" ")}
            >
              {uploadingFiles
                ? "Uploading..."
                : isPersonalisationIncomplete
                  ? "Complete required fields"
                  : "Add to cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}